import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useGetProblems, useDeleteProblem } from "../../hooks/useProblems";
import ProblemsTable from "./ProblemsTable";
import AddProblemModal from "./AddProblemModal";
import ViewNoteModal from "./ViewNoteModal"; // Add this import
import Cookies from "js-cookie";
import { Plus, Trash2, Dices } from "lucide-react";
import toast from "react-hot-toast";

const Problems = () => {
    const navigate = useNavigate();

    const { data: problems = [], isLoading, isError } = useGetProblems();
    const deleteMutation = useDeleteProblem();

    const [selectedIds, setSelectedIds] = useState([]);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [problemToEdit, setProblemToEdit] = useState(null);
    const [problemToViewNote, setProblemToViewNote] = useState(null); // Add this state

    const solvedCount = useMemo(() => problems.filter(p => p.status).length, [problems]);
    const progressPercent = problems.length > 0 ? Math.round((solvedCount / problems.length) * 100) : 0;

    if (isError || !Cookies.get("accessToken")) {
        Cookies.remove("accessToken");
        Cookies.remove("refreshToken");
        navigate("/login");
        return null;
    }


    const handleDeleteSelected = () => {
        if (window.confirm("Are you sure you want to delete the selected problems?")) {
            deleteMutation.mutate(selectedIds, {
                onSuccess: () => setSelectedIds([])
            });
        }
    };

    const openEditModal = (problem) => {
        setProblemToEdit(problem);
        setIsAddModalOpen(true);
    };

    const closeAddModal = () => {
        setProblemToEdit(null);
        setIsAddModalOpen(false);
    };

    const pickRandom = () => {
        if (problems.length === 0) return;
        const randomProb = problems[Math.floor(Math.random() * problems.length)];
        toast.success(`Try this one: ${randomProb.title}`, { duration: 5000, icon: '🎲' });
        window.open(randomProb.url, '_blank');
    };

    return (
        <div className="max-w-7xl mx-auto space-y-6">
            {/* Header & Controls */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">

                {/* Left Side: Stats & Delete */}
                <div className="flex items-center gap-6">
                    <div className="flex flex-col">
                        <span className="text-sm font-medium text-slate-500">Progress</span>
                        <div className="flex items-center gap-3">
                            <div className="w-32 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-emerald-500 transition-all duration-500"
                                    style={{ width: `${progressPercent}%` }}
                                />
                            </div>
                            <span className="text-sm font-bold text-slate-700">{progressPercent}%</span>
                        </div>
                    </div>

                    {selectedIds.length > 0 && (
                        <div className="h-8 w-px bg-slate-200 hidden sm:block"></div>
                    )}

                    {selectedIds.length > 0 && (
                        <button
                            onClick={handleDeleteSelected}
                            disabled={deleteMutation.isPending}
                            className="flex items-center gap-2 px-3 py-1.5 bg-red-50 text-red-600 text-sm font-semibold rounded-lg hover:bg-red-100 transition-colors"
                        >
                            <Trash2 className="w-4 h-4" />
                            Delete ({selectedIds.length})
                        </button>
                    )}
                </div>

                {/* Right Side: Actions */}
                <div className="flex items-center gap-3 w-full sm:w-auto">
                    <button
                        onClick={pickRandom}
                        className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 text-slate-600 font-medium hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        <Dices className="w-4 h-4" />
                        Random
                    </button>
                    <button
                        onClick={() => setIsAddModalOpen(true)}
                        className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 shadow-sm transition-colors"
                    >
                        <Plus className="w-4 h-4" />
                        Add Problem
                    </button>
                </div>
            </div>

            {/* Main Table Area */}
            {isLoading ? (
                <div className="w-full h-96 bg-white border border-slate-200 rounded-2xl animate-pulse flex items-center justify-center text-slate-400">
                    Loading your problems...
                </div>
            ) : (
                <ProblemsTable
                    data={problems}
                    onSelectionChange={setSelectedIds}
                    onEdit={openEditModal}
                    onViewNote={setProblemToViewNote} // Pass it here!
                />
            )}

            {/* Add / Edit Modal Component */}
            {isAddModalOpen && (
                <AddProblemModal
                    isOpen={isAddModalOpen}
                    onClose={closeAddModal}
                    initialData={problemToEdit}
                />
            )}

            {/* View Note Modal */}
            <ViewNoteModal
                isOpen={!!problemToViewNote}
                onClose={() => setProblemToViewNote(null)}
                problem={problemToViewNote}
            />
        </div>
    );
};

export default Problems;